import { InjectionToken } from '@angular/core';

import type { AppointmentSnapshot, OfficeAdapterEvents, OfficeThemeMode, OutlookContext, PaneInitializationContext } from './office-types';

/**
 * The single boundary to Office.js. Two implementations:
 *  - RealOfficeAdapter — the production adapter over `window.Office`.
 *  - SimOfficeAdapter — the dev-harness adapter driven by the in-app
 *    Outlook simulator (`/#/sim`), which implements the same item contract.
 */
export abstract class OfficeAdapter {
  /** Which Outlook surface hosts the pane right now. */
  abstract context(): OutlookContext;

  /** The signed-in Outlook user's email (Office.context.mailbox.userProfile). */
  abstract userEmail(): string | null;

  /** True when the host item can be written (AppointmentCompose). */
  abstract canWriteItem(): boolean;

  /** Read the full item state (times, locations, recipients). */
  abstract readAppointment(): Promise<AppointmentSnapshot | null>;

  /** item.start/end.setAsync — retime preserving nothing but what's given. */
  abstract setAppointmentTimes(start: Date, end: Date): Promise<void>;

  /** enhancedLocation.addAsync({ id: roomEmail, type: 'room' }). */
  abstract addRoomLocation(roomEmail: string, displayName: string): Promise<void>;

  /** enhancedLocation.removeAsync({ id: roomEmail }). */
  abstract removeRoomLocation(roomEmail: string): Promise<void>;

  /** Subscribe to host events; returns an unsubscribe. */
  abstract on<E extends keyof OfficeAdapterEvents>(event: E, handler: OfficeAdapterEvents[E]): () => void;

  /** Office.context.officeTheme — null below Mailbox 1.14. */
  abstract officeTheme(): OfficeThemeMode | null;

  /** getInitializationContextAsync — Smart Alert / insight handover. */
  abstract initializationContext(): Promise<PaneInitializationContext | null>;

  /** displayAppointmentForm / native edit affordance (read → compose). */
  abstract openItemForEditing(): Promise<void>;

  /** notificationMessages.addAsync (InsightMessage with ShowTaskPane action). */
  abstract showInsightMessage(key: string, message: string): Promise<void>;

  /** window.open deep link to the employee app (map, approvals). */
  openDeepLink(url: string): void {
    window.open(url, '_blank', 'noopener');
  }
}

export const OFFICE_ADAPTER = new InjectionToken<OfficeAdapter>('OFFICE_ADAPTER');
